import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { User } from '@/types'
import { authService } from '@/services/authService'
import { phoneVerificationService } from '@/services/phoneVerificationService'

export interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  register: (data: {
    email: string
    password: string
    firstName: string
    lastName: string
    phoneNumber?: string
  }) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  signOutUser: () => Promise<void>
  updateUser: (userData: Partial<User>) => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if user is already authenticated
        if (authService.isAuthenticated()) {
          const currentUser = authService.getCurrentUser()
          if (currentUser) {
            // Use cached user data first to prevent loading state
            setUser(currentUser)

            // Then refresh user data from API in background
            try {
              const refreshResponse = await authService.refreshUserData()
              if (refreshResponse.success && refreshResponse.user) {
                setUser(refreshResponse.user)
              }
            } catch (refreshError) {
              console.warn('Failed to refresh user data, using cached data:', refreshError)
              // Keep using cached user data
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        localStorage.removeItem('userData')
        setUser(null)
      } finally {
        // Always set loading to false, even if refresh fails
        setLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true)
      const response = await authService.login({ email, password })

      if (response.success && response.user) {
        setUser(response.user)
        return { success: true }
      } else {
        return {
          success: false,
          error: response.error?.message || 'Erreur lors de la connexion'
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erreur inattendue lors de la connexion'
      }
    } finally {
      setLoading(false)
    }
  }

  const register = async (data: {
    email: string
    password: string
    firstName: string
    lastName: string
    phoneNumber?: string
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true)
      const response = await authService.register(data)

      if (response.success && response.user) {
        setUser(response.user)
        return { success: true }
      } else {
        return {
          success: false,
          error: response.error?.message || 'Erreur lors de l\'inscription'
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erreur inattendue lors de l\'inscription'
      }
    } finally {
      setLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    try {
      setLoading(true)
      await authService.logout()
      phoneVerificationService.clearStatus()
      setUser(null)
    } catch (error) {
      console.error('Logout error:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const updateUser = (userData: Partial<User>): void => {
    if (user) {
      const updatedUser = { ...user, ...userData }
      setUser(updatedUser)
      authService.updateCurrentUser(userData)
    }
  }

  // isAuthenticated est dérivé de l'état React `user` (et non du localStorage)
  // pour garantir la réactivité après login/logout.
  // On conserve authService.isAuthenticated() comme fallback uniquement pendant
  // l'initialisation (avant que `user` ne soit hydraté).
  const isAuthenticated = user !== null || authService.isAuthenticated()

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    signOutUser: logout, // Alias pour la compatibilité avec le code existant
    updateUser,
    isAuthenticated
  }

  return React.createElement(
    AuthContext.Provider,
    { value },
    children
  )
}